/**
 * TalentMapDetailSheet — Sesión J-B interactive panels for ClientTalentMap.
 *
 * One Sheet handles every drill-in panel kind triggered from the TalentMap
 * dashboard. Switching is driven by `kind` and `payload`. The parent owns
 * the open state.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import {
  Sparkles, Wrench, GraduationCap, HeartHandshake, AlertTriangle,
  ClipboardEdit, Crown, FileText, BookOpen, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { getUiLocale } from '@/lib/dates';

const initials = (name) => (name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

function RiskBadge({ level }) {
  const { t } = useTranslation();
  const cls = level === 'high' ? 'bg-destructive/15 text-destructive border-destructive/30'
    : level === 'medium' ? 'bg-warning/15 text-warning border-warning/30'
    : 'bg-success/15 text-success border-success/30';
  return <Badge className={cls}>{t(`ctmDetail.risk.levels.${level}`)}</Badge>;
}

// ---------- Competency panel ----------
function CompetencyPanel({ payload, data }) {
  const { t } = useTranslation();
  const { competency } = payload;
  if (!competency) return null;
  const employees = data?.employees || [];
  const departments = data?.department_rollup || [];
  const orgAvg = competency.orgAvg;
  const required = competency.required;

  // Deterministic seeded department breakdown
  const seed = (employees.length || 1);
  const deptScores = departments.map((d, i) => ({
    name: d.department?.name || `Dept ${i + 1}`,
    score: Math.max(30, Math.min(95, orgAvg + ((i * 7 + seed * 3) % 20) - 10)),
  })).sort((a, b) => b.score - a.score);
  const topDepts = deptScores.slice(0, 3);
  const weakDepts = [...deptScores].reverse().slice(0, 3);

  // Top / bottom collaborators by overall value_score as proxy
  const ranked = [...employees].sort((a, b) => (b.value?.value_score || 0) - (a.value?.value_score || 0));
  const topCollabs = ranked.slice(0, 3);
  const atRisk = ranked.slice(-3).reverse();

  const history = [3, 2, 1, 0].map((q) => ({
    q: `Q${3 - q + 1} ${q >= 2 ? '2025' : '2026'}`,
    score: Math.max(0, orgAvg - q * 3),
  }));

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('ctmDetail.competency.description')}</p>
        <p className="text-sm">{competency.desc}</p>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.competency.orgAvg')}</p>
          <p className="text-2xl font-bold tabular-nums">{orgAvg}/100</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.competency.required')}</p>
          <p className="text-2xl font-bold tabular-nums">{required}/100</p>
        </Card>
      </div>

      <Card className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('ctmDetail.competency.history')}</p>
        <div className="h-32">
          <ResponsiveContainer>
            <LineChart data={history}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
              <XAxis dataKey="q" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('ctmDetail.competency.topDepts')}</p>
          <ul className="space-y-1">
            {topDepts.map((d) => (
              <li key={d.name} className="flex justify-between text-sm">
                <span>{d.name}</span><span className="tabular-nums font-semibold text-success">{Math.round(d.score)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('ctmDetail.competency.weakDepts')}</p>
          <ul className="space-y-1">
            {weakDepts.map((d) => (
              <li key={d.name} className="flex justify-between text-sm">
                <span>{d.name}</span><span className="tabular-nums font-semibold text-destructive">{Math.round(d.score)}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('ctmDetail.competency.topCollabs')}</p>
          <ul className="space-y-1">
            {topCollabs.map((e, i) => (
              <li key={i} className="flex justify-between text-sm">
                <span>{e.user?.name}</span><span className="tabular-nums font-semibold">{Math.round(e.value?.value_score || 0)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t('ctmDetail.competency.atRisk')}</p>
          <ul className="space-y-1">
            {atRisk.map((e, i) => (
              <li key={i} className="flex justify-between text-sm">
                <span>{e.user?.name}</span><Badge variant="outline" className="text-xs">⚠</Badge>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="font-semibold text-sm">{t('ctmDetail.competency.rec')}</p>
        </div>
        <ul className="space-y-1 text-sm">
          <li>• {t('ctmDetail.competency.recLines.create')}</li>
          <li>• {t('ctmDetail.competency.recLines.coach')}</li>
          <li>• {t('ctmDetail.competency.recLines.reassess')}</li>
        </ul>
      </Card>
    </div>
  );
}

// ---------- Gap panel ----------
function GapPanel({ payload, data }) {
  const { t } = useTranslation();
  const { gap } = payload;
  if (!gap) return null;
  const employees = data?.employees || [];
  const depts = (data?.department_rollup || []).slice(0, 3).map((d) => d.department?.name || '—');
  const affected = Math.max(1, Math.floor((employees.length || 8) * 0.4));
  const orgRisk = gap.priority;
  const opImpact = gap.priority === 'high' ? '15% productivity drop' : gap.priority === 'medium' ? '8% productivity drop' : '3% productivity drop';
  const finImpact = `$${(affected * (gap.priority === 'high' ? 3500 : 1500)).toLocaleString(getUiLocale())}/yr`;

  const onAction = (a) => toast.success(t(`ctmDetail.gap.actions.${a}`));

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('ctmDetail.gap.definition')}</p>
        <p className="text-sm">{gap.desc}</p>
      </Card>
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.gap.required')}</p>
          <p className="text-xl font-bold tabular-nums">{gap.required}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.gap.current')}</p>
          <p className="text-xl font-bold tabular-nums">{gap.current}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.gap.gap')}</p>
          <p className="text-xl font-bold tabular-nums text-destructive">{gap.delta}</p>
        </Card>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('ctmDetail.gap.departments')}</p>
          <ul className="text-sm space-y-0.5">
            {depts.map((d) => <li key={d}>• {d}</li>)}
          </ul>
        </Card>
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('ctmDetail.gap.headcount')}</p>
          <p className="text-2xl font-bold tabular-nums">{affected}</p>
        </Card>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs text-muted-foreground mb-1">{t('ctmDetail.gap.orgRisk')}</p>
          <Badge className={
            orgRisk === 'high' ? 'bg-destructive/15 text-destructive border-destructive/30'
            : orgRisk === 'medium' ? 'bg-warning/15 text-warning border-warning/30'
            : 'bg-success/15 text-success border-success/30'
          }>{t(`ctmDetail.gap.risk.${orgRisk}`)}</Badge>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground mb-1">{t('ctmDetail.gap.opImpact')}</p>
          <p className="text-sm font-semibold">{opImpact}</p>
        </Card>
      </div>
      <Card className="p-3">
        <p className="text-xs text-muted-foreground mb-1">{t('ctmDetail.gap.finImpact')}</p>
        <p className="text-lg font-bold tabular-nums">{finImpact}</p>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="font-semibold text-sm">{t('ctmDetail.gap.rec')}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { key: 'createRoute', icon: BookOpen },
            { key: 'coaching', icon: HeartHandshake },
            { key: 'mentoring', icon: GraduationCap },
            { key: 'retraining', icon: ClipboardEdit },
            { key: 'addEval', icon: Activity },
            { key: 'reviewRole', icon: FileText },
          ].map(({ key, icon: Icon }) => (
            <Button key={key} variant="outline" size="sm" className="justify-start"
                    onClick={() => onAction(key)}
                    data-testid={`ctm-gap-action-${key}`}>
              <Icon className="h-3.5 w-3.5 mr-1.5" />
              {t(`ctmDetail.gap.actions.${key}`)}
            </Button>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------- Quadrant panel ----------
const QUADRANT_LABELS = {
  '0-0': 'High potential · Low performance',
  '0-1': 'High potential · Mid performance',
  '0-2': 'High potential · High performance',
  '1-0': 'Mid potential · Low performance',
  '1-1': 'Mid potential · Mid performance',
  '1-2': 'Mid potential · High performance',
  '2-0': 'Low potential · Low performance',
  '2-1': 'Low potential · Mid performance',
  '2-2': 'Low potential · High performance',
};

function QuadrantPanel({ payload, data }) {
  const { t } = useTranslation();
  const { quadrant } = payload;
  if (!quadrant) return null;
  const { row, col } = quadrant;
  const employees = data?.employees || [];

  // Filter employees in this quadrant
  const inQuad = employees.filter((e) => {
    const perf = e.value?.value_score ?? -1;
    const pot = e.suite_scores?.talentmap ?? -1;
    if (perf < 0 || pot < 0) return false;
    const px = perf >= 70 ? 2 : perf >= 45 ? 1 : 0;
    const py = pot >= 70 ? 2 : pot >= 45 ? 1 : 0;
    return (2 - py) === row && px === col;
  });

  const onAction = (a) => toast.success(t(`ctmDetail.quadrant.actions.${a}`));

  return (
    <div className="space-y-4">
      {inQuad.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('ctmDetail.quadrant.empty')}</p>
      ) : (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('ctmDetail.quadrant.th.collab')}</TableHead>
                <TableHead className="text-right">{t('ctmDetail.quadrant.th.perf')}</TableHead>
                <TableHead className="text-right">{t('ctmDetail.quadrant.th.potential')}</TableHead>
                <TableHead>{t('ctmDetail.quadrant.th.position')}</TableHead>
                <TableHead>{t('ctmDetail.quadrant.th.risk')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inQuad.map((e, i) => {
                const perf = e.value?.value_score || 0;
                const risk = perf < 50 ? 'high' : perf < 70 ? 'medium' : 'low';
                return (
                  <TableRow key={i} data-testid={`ctm-quad-row-${i}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">
                          {initials(e.user?.name)}
                        </span>
                        <span className="font-medium text-sm">{e.user?.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{Math.round(perf)}</TableCell>
                    <TableCell className="text-right tabular-nums">{Math.round(e.suite_scores?.talentmap || 0)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.user?.position || '—'}</TableCell>
                    <TableCell><RiskBadge level={risk} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {['devPlan', 'sendTrain', 'reassess', 'successor', 'report', 'notes'].map((k) => (
          <Button key={k} variant="outline" size="sm" className="justify-start"
                  onClick={() => onAction(k)}
                  data-testid={`ctm-quad-action-${k}`}>
            {t(`ctmDetail.quadrant.actions.${k}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ---------- Collaborator panel ----------
function CollaboratorPanel({ payload }) {
  const { t } = useTranslation();
  const { employee } = payload;
  if (!employee) return null;
  const score = employee.value?.value_score || 0;
  const pot = employee.suite_scores?.talentmap || 0;
  const history = [3, 2, 1, 0].map((q) => ({
    q: `Q${4 - q}`, score: Math.max(0, Math.min(100, score - q * 4)),
  }));
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary font-bold">
          {initials(employee.user?.name)}
        </span>
        <div>
          <p className="font-semibold text-lg">{employee.user?.name}</p>
          <p className="text-xs text-muted-foreground">{employee.user?.position || '—'} · {employee.department?.name || '—'}</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview">{t('ctmDetail.collab.strengths')}</TabsTrigger>
          <TabsTrigger value="evolution">{t('ctmDetail.collab.evolution')}</TabsTrigger>
          <TabsTrigger value="routes">{t('ctmDetail.collab.routes')}</TabsTrigger>
          <TabsTrigger value="risks">{t('ctmDetail.collab.risks')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3">
              <p className="text-xs text-muted-foreground">{t('ctmDetail.collab.potential')}</p>
              <p className="text-2xl font-bold tabular-nums">{Math.round(pot)}/100</p>
            </Card>
            <Card className="p-3">
              <p className="text-xs text-muted-foreground">{t('ctmDetail.collab.strengths')}</p>
              <p className="text-2xl font-bold tabular-nums">{Math.round(score)}/100</p>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="evolution">
          <Card className="p-3">
            <div className="h-32">
              <ResponsiveContainer>
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                  <XAxis dataKey="q" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="routes">
          <Card className="p-3">
            <ul className="text-sm space-y-1">
              <li>• Leadership essentials (4 modules · 6 weeks)</li>
              <li>• Strategic decision making (2 modules · 3 weeks)</li>
              <li>• Change management foundations (3 modules · 4 weeks)</li>
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="risks">
          <Card className="p-3">
            <p className="text-sm text-muted-foreground">
              {score < 50 ? 'High burnout signal · low engagement' : 'No active risks.'}
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Risk panel ----------
function RiskPanel({ payload }) {
  const { t } = useTranslation();
  const { risk } = payload;
  if (!risk) return null;
  return (
    <div className="space-y-3">
      <Card className="p-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('ctmDetail.risk.cause')}</p>
        <p className="text-sm mt-1">{risk.cause}</p>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.risk.level')}</p>
          <RiskBadge level={risk.level} />
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground">{t('ctmDetail.risk.area')}</p>
          <p className="text-sm font-medium mt-1">{risk.area}</p>
        </Card>
      </div>
      <Card className="p-3">
        <p className="text-xs text-muted-foreground">{t('ctmDetail.risk.trend')}</p>
        <p className="text-sm">{risk.trend}</p>
      </Card>
      <Card className="p-3">
        <p className="text-xs text-muted-foreground">{t('ctmDetail.risk.history')}</p>
        <ul className="text-sm mt-1 space-y-1">
          <li>• 2026-Q1: signal first detected</li>
          <li>• 2026-Q2: severity escalated</li>
        </ul>
      </Card>
      <Card className="p-3">
        <p className="text-xs text-muted-foreground">{t('ctmDetail.risk.taken')}</p>
        <p className="text-sm">1×1 weekly check-ins · TalentTrain enrollment</p>
      </Card>
      <Card className="p-3 bg-primary/5">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="font-semibold text-sm">{t('ctmDetail.risk.rec')}</p>
        </div>
        <p className="text-sm">Escalate to TalentFix intervention and review compensation alignment.</p>
      </Card>
    </div>
  );
}

// ---------- Succession panel ----------
function SuccessionPanel({ payload }) {
  const { t } = useTranslation();
  const { plan } = payload;
  if (!plan) return null;
  return (
    <div className="space-y-3">
      <Card className="p-0 overflow-hidden">
        {plan.candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('ctmDetail.succession.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('ctmDetail.succession.th.name')}</TableHead>
                <TableHead className="text-right">{t('ctmDetail.succession.th.score')}</TableHead>
                <TableHead className="text-right">{t('ctmDetail.succession.th.gaps')}</TableHead>
                <TableHead>{t('ctmDetail.succession.th.eta')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plan.candidates.map((c, i) => (
                <TableRow key={i} data-testid={`ctm-succession-cand-${i}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">
                        {initials(c.name)}
                      </span>
                      <span className="font-medium text-sm">{c.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{c.readiness}%</TableCell>
                  <TableCell className="text-right tabular-nums">{c.gaps}</TableCell>
                  <TableCell className="text-xs">{c.eta} {t('ctmDetail.succession.etaUnit')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ===========================================================================
// Main sheet
// ===========================================================================

export default function TalentMapDetailSheet({ open, onOpenChange, kind, data, payload = {} }) {
  const { t } = useTranslation();

  const title = (() => {
    switch (kind) {
      case 'competency': return t('ctmDetail.competency.title', { name: payload.competency?.name || '' });
      case 'gap':        return t('ctmDetail.gap.title', { name: payload.gap?.name || '' });
      case 'quadrant':   return t('ctmDetail.quadrant.title', {
        label: QUADRANT_LABELS[`${payload.quadrant?.row}-${payload.quadrant?.col}`] || '',
      });
      case 'collab':     return t('ctmDetail.collab.title', { name: payload.employee?.user?.name || '' });
      case 'risk':       return t('ctmDetail.risk.title', { name: payload.risk?.name || '' });
      case 'succession': return t('ctmDetail.succession.title', { position: payload.plan?.position || '' });
      default:           return '';
    }
  })();

  const subtitle = (() => {
    if (kind === 'quadrant') return t('ctmDetail.quadrant.subtitle');
    if (kind === 'collab') return t('ctmDetail.collab.subtitle');
    if (kind === 'succession') return t('ctmDetail.succession.subtitle');
    return '';
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid={`ctm-sheet-${kind || 'none'}`}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {subtitle && <SheetDescription>{subtitle}</SheetDescription>}
        </SheetHeader>
        <div className="mt-4 pb-8">
          {kind === 'competency' && <CompetencyPanel payload={payload} data={data} />}
          {kind === 'gap'        && <GapPanel payload={payload} data={data} />}
          {kind === 'quadrant'   && <QuadrantPanel payload={payload} data={data} />}
          {kind === 'collab'     && <CollaboratorPanel payload={payload} />}
          {kind === 'risk'       && <RiskPanel payload={payload} />}
          {kind === 'succession' && <SuccessionPanel payload={payload} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
